import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { autoBlockText, closeIncidentFalsePositive, incidentActionsQuery, undoIncidentFalsePositive, incidentQuery, incidentsTableQuery, openIncidentTicket, unblockIncidentAutoBlock, type IncidentAction, type IncidentFilter, type SecuriteeIncidentDetail, type SecuriteeIncidentRow } from '../api/securitee'
import { Modal } from '../ui/Modal'
import { DeviceFilterChip } from './DeviceFilterChip'
import { Content } from '../ui/Shell'
import { EdrDroppedAlert } from './EdrDroppedAlert'
import { SocMetricsStrip } from './SocMetricsStrip'
import { XdrFlowAlert } from './XdrFlowAlert'
import { DataTable, useTableState } from '../ui/DataTable'
import { useHeldOpenRow } from '../ui/useHeldOpenRow'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { RansomwareResponse } from './RansomwareResponse'
import { ResponseActionsList } from './ResponseActionsList'
import { IncidentTicketBox } from './IncidentTicketBox'
import { RespondSessions } from './RespondSessions'
import { QueueFreshnessBar } from './QueueFreshnessBar'
import { useQueueFreshness } from './useQueueFreshness'
import { incidentColumns, incidentStatusHtml, severityHtml } from './columns'
import { DeviceLinks } from './IncidentDeviceLinks'
import { XdrChain } from '../easeedesk/XdrChain'
import './incidents.css'

/**
 * Securitee > Incidents - detectiile XDR stranse pe aparat de ph4.api
 * (`IncidentGrouper`), ordonate dupa ultima detectie. Ecran nou, fara pereche in
 * portalul vechi; in meniu ca Detections (dreptul + EDR pornit pe firma).
 *
 * Randul desfacut e panoul incidentului: detectiile lui si tichetul. Aici NU e
 * tichetare - lucrul pe incident sta in Easeedesk: Medium+ primesc tichet
 * automat, Low au "Open ticket"; starea incidentului e a tichetului. Panoul
 * are Respond: actiunile pe aparatul incidentului (izolare / eliberare, oprire,
 * aplicatiile active, log off pe utilizatorii logati), aceleasi capete si
 * drepturi ca filele Antiransomware si Active Sessions.
 * Izolarea interna / externa, logoff si blocarea ecranului vin cu agentul
 * (docs XDR 01).
 */
export const TABLE_ID = 'securiteeIncidents'

/** Coada goala, aceeasi referinta intre randari (semnele New / Updated se recalculeaza pe ea). */
const NO_ROWS: SecuriteeIncidentRow[] = []

const FILTERS: { value: IncidentFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
]

/**
 * `device`: coada vine din legatura "Its incidents" (`?device=`); `onClearDevice` o scoate din adresa.
 * `incident`: din legatura "Incident #N" a unei detectii (`?incident=`): numai el, deschis.
 */
export function SecuriteeIncidentsPage({ device = 0, onClearDevice, incident = 0, onClearIncident }: { device?: number; onClearDevice?(): void; incident?: number; onClearIncident?(): void } = {}) {
  // cele mai grave intai (apoi cele mai noi, pe server): un Critical nu sta sub zece Low
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 3, dir: 'desc' }, search: '' })
  const [status, setStatus] = useState<IncidentFilter>('open')
  // ordonarea nu depinde de coloana "Company" (n-are ordonare), deci se alege din lista de baza
  const base = useMemo(() => incidentColumns(), [])
  const orderColumn = base.find((c) => c.index === state.order.column && c.orderable) ?? base[3]!
  const query = useQuery({
    ...incidentsTableQuery(
      { draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search },
      status,
      device,
      incident,
    ),
    placeholderData: keepPreviousData,
    // ecranul SOC sta deschis toata tura; React Query nu reciteste cat fila e ascunsa
    refetchInterval: 60_000,
  })

  const client = useQueryClient()
  // "Open ticket" din rand (HTML-ul randului, deci clic delegat)
  const rowTicket = useMutation({
    mutationFn: (id: number) => openIncidentTicket(id),
    onSuccess: (r) => {
      toast(`Ticket #${r.ticket_id} opened.`, 'Ticket', 'success')
      void client.invalidateQueries({ queryKey: ['securitee', 'incidents'] })
    },
    onError: (e: Error) => toast(e.message, 'Ticket', 'error'),
  })
  // "False positive" din rand: numai dupa confirmare
  // `ticket` > 0: incidentul are tichet, care pleaca in `fpStatus` ('' = ramane deschis)
  const [falsePositive, setFalsePositive] = useState<{ id: number; device: string; title: string; ticket: number; fpStatus: string } | null>(null)
  // banda cu Undo, cat tine fereastra din XDR settings
  const [undoable, setUndoable] = useState<{ id: number; device: string; title: string; ticket: number; moved: string; minutes: number } | null>(null)
  useEffect(() => {
    if (!undoable) return
    const timer = window.setTimeout(() => setUndoable(null), undoable.minutes * 60_000)
    return () => window.clearTimeout(timer)
  }, [undoable])
  const closeFalse = useMutation({
    mutationFn: (id: number) => closeIncidentFalsePositive(id),
    onSuccess: (r) => {
      if (falsePositive) setUndoable({ ...falsePositive, moved: r.ticket_status ?? '', minutes: r.undo_minutes })
      setFalsePositive(null)
      void client.invalidateQueries({ queryKey: ['securitee', 'incidents'] })
    },
    onError: (e: Error) => toast(e.message, 'Incident', 'error'),
  })
  const undo = useMutation({
    mutationFn: (id: number) => undoIncidentFalsePositive(id),
    onSuccess: (r, id) => {
      setUndoable(null)
      toast(undoMessage(id, r), 'Incident', 'success')
      void client.invalidateQueries({ queryKey: ['securitee', 'incidents'] })
    },
    onError: (e: Error) => toast(e.message, 'Incident', 'error'),
  })
  const onRowClick = (e: MouseEvent<HTMLDivElement>) => {
    const button = (e.target as HTMLElement).closest<HTMLButtonElement>('.incident-row-open-ticket')
    if (button && !rowTicket.isPending) rowTicket.mutate(Number(button.dataset.id))
    const fp = (e.target as HTMLElement).closest<HTMLButtonElement>('.incident-row-false-positive')
    if (fp) setFalsePositive({ id: Number(fp.dataset.id), device: fp.dataset.device ?? '', title: fp.dataset.title ?? '', ticket: Number(fp.dataset.ticket ?? 0), fpStatus: fp.dataset.fpStatus ?? '' })
    const back = (e.target as HTMLElement).closest<HTMLButtonElement>('.incident-row-undo')
    if (back && !undo.isPending) undo.mutate(Number(back.dataset.id))
  }

  // ce s-a schimbat de la ultima privire: New / Updated in rand, in capul listei si in titlu
  const { fresh, markAllSeen } = useQueueFreshness(query.data?.data ?? NO_ROWS, query.isSuccess)
  const fetched = useMemo(() => (query.data?.data ?? NO_ROWS).map((r) => (fresh.has(r.ID) ? { ...r, __fresh: fresh.get(r.ID) } : r)), [query.data, fresh])
  // coada se reciteste la minut: incidentul deschis nu se inchide la fiecare recitire
  const { rows, openRow, onOpenRow } = useHeldOpenRow(fetched, JSON.stringify([status, device, incident, state.start, state.length, state.order, state.search]))
  // venit din "Incident #N": se deschide o singura data, cand ajunge randul
  const opened = useRef(false)
  useEffect(() => {
    if (incident <= 0 || opened.current || !rows.some((r) => r.DT_RowId === `row_${incident}`)) return
    opened.current = true
    onOpenRow(`row_${incident}`)
  }, [incident, rows, onOpenRow])

  const allCompanies = query.data?.all_companies === true
  const canFalsePositive = query.data?.can_false_positive === true
  // dreptul de a muta si tichetul vine tot cu coada
  const canTicketFalsePositive = query.data !== undefined && 'can_false_positive_ticket' in query.data && query.data.can_false_positive_ticket === true
  const columns = useMemo(() => incidentColumns(allCompanies, canFalsePositive, canTicketFalsePositive), [allCompanies, canFalsePositive, canTicketFalsePositive])

  return (
    <Content>
      <div id="content" className="xdr-incidents" onClick={onRowClick}>
        <XdrFlowAlert />
        <EdrDroppedAlert />
        <SocMetricsStrip />
        {undoable && (
          <div className="alert alert-info incident-fp-undo" role="status">
            Closed as a false positive: <strong>{undoable.title}</strong> on <strong>{undoable.device || 'the device'}</strong>.
            {undoable.ticket > 0 && (undoable.moved ? ` Ticket #${undoable.ticket} went to ${undoable.moved}.` : ` Ticket #${undoable.ticket} stays open.`)} You can undo it for {undoable.minutes} minutes.{' '}
            <button type="button" className="btn btn-xs btn-primary incident-fp-undo-go" disabled={undo.isPending} onClick={() => undo.mutate(undoable.id)}>
              Undo
            </button>{' '}
            <button type="button" className="close" aria-label="Hide" onClick={() => setUndoable(null)}>
              ×
            </button>
          </div>
        )}
        {incident > 0 && <DeviceFilterChip id={incident} label="Incident" clearTitle="Show the whole queue" onClear={onClearIncident} />}
        {device > 0 && incident === 0 && <DeviceFilterChip id={device} name={query.data?.data[0]?.device_name} onClear={onClearDevice} />}
        {/* deasupra listei: in bara albastra de sus era greu de citit si o taia selectorul de firma */}
        <QueueFreshnessBar updatedAt={query.dataUpdatedAt} fresh={fresh} onMarkSeen={markAllSeen} />
        <DataTable
          id={TABLE_ID}
          columns={columns}
          rows={rows}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          headerButtons={
            incident > 0 ? undefined : (
              <select
                id="incidentStatus"
                className="form-control input-sm tk-header-extra"
                aria-label="Status"
                style={{ width: 'auto', display: 'inline-block' }}
                value={status}
                onChange={(e) => {
                  setStatus(FILTERS.find((f) => f.value === e.target.value)?.value ?? 'open')
                  setState({ ...state, start: 0 })
                }}
              >
                {FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            )
          }
          details={(row) => <IncidentPanel id={Number(row.ID)} />}
          openRow={openRow}
          onOpenRow={onOpenRow}
        />
        <Modal
          id="incident-false-positive"
          open={falsePositive !== null}
          onClose={() => setFalsePositive(null)}
          title="Close as a false positive?"
          footer={
            <>
              <button type="button" className="btn btn-default" onClick={() => setFalsePositive(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" id="incident-false-positive-confirm" disabled={closeFalse.isPending} onClick={() => falsePositive && closeFalse.mutate(falsePositive.id)}>
                Yes, false positive
              </button>
            </>
          }
        >
          {falsePositive && falsePositive.ticket > 0 ? (
            <p>
              <strong>{falsePositive.title}</strong> on <strong>{falsePositive.device || 'the device'}</strong> leaves the Open list.{' '}
              {falsePositive.fpStatus ? `Ticket #${falsePositive.ticket} goes to ${falsePositive.fpStatus} and gets a comment.` : `Ticket #${falsePositive.ticket} stays open: close it in Easeedesk.`}
            </p>
          ) : (
            <p>
              <strong>{falsePositive?.title}</strong> on <strong>{falsePositive?.device || 'the device'}</strong> leaves the Open list and gets no ticket.
            </p>
          )}
          <p className="text-muted">New detections on the device open a new incident.</p>
        </Modal>
      </div>
    </Content>
  )
}

/** Ce a facut Undo: incidentele venite intre timp pe aparat se strang inapoi (in cel cu tichet, daca e unul). */
export function undoMessage(id: number, r: { incident_id: number; merged: number }): string {
  if (r.merged < 1) return 'The incident is open again.'
  if (r.incident_id !== id) return `The incident is open again, joined into incident #${r.incident_id}, which has the ticket.`
  return `The incident is open again and took back ${r.merged === 1 ? 'the newer incident' : `the ${r.merged} newer incidents`} from the device.`
}

function IncidentPanel({ id }: { id: number }) {
  const client = useQueryClient()
  const query = useQuery(incidentQuery(id))
  const refreshActions = () => void client.invalidateQueries({ queryKey: incidentActionsQuery(id).queryKey })
  // detectia cu lantul deschis; unul odata, ca panoul sa nu creasca fara capat
  const [chainOf, setChainOf] = useState<number | null>(null)

  // numai panoul se reciteste: o recitire a listei ar inchide randul desfacut
  const ticket = useMutation({
    mutationFn: () => openIncidentTicket(id),
    onSuccess: () => client.invalidateQueries({ queryKey: incidentQuery(id).queryKey }),
    onError: (e: Error) => toast(e.message, 'Ticket', 'error'),
  })

  if (query.isPending) return <Spinner />
  if (query.isError) return <p className="color-red">{(query.error as Error).message}</p>

  const { incident, detections, can_ticket } = query.data
  const recommended = query.data.recommended ?? null
  const device = incident.device_name || 'device #' + incident.device_id

  return (
    <div className="incident-panel xdr-incident">
      <div className="xdr-strip">
        <div className="xdr-strip-meta">
          <span dangerouslySetInnerHTML={{ __html: incidentStatusHtml(incident.status) }} />
          <span className="xdr-mono">
            <i className="far fa-clock" aria-hidden="true"></i> {incident.first_detected_at} – {incident.last_detected_at} UTC
          </span>
          {incident.ticket_id ? (
            <IncidentTicketBox incident={incident} canTake={query.data.can_take === true} canFalsePositive={query.data.can_false_positive === true && query.data.can_false_positive_ticket === true} />
          ) : (
            can_ticket && (
              <button type="button" className="btn btn-xs btn-primary incident-ticket" disabled={ticket.isPending} onClick={() => ticket.mutate()}>
                Open ticket
              </button>
            )
          )}
          <DeviceLinks id={incident.device_id} name={device} at={incident.last_detected_at} />
        </div>
        {recommended && <Recommended r={recommended} />}
      </div>

      {incident.ticket_missing && (
        <div className="alert alert-danger incident-ticket-missing-band" role="alert">
          The ticket was not opened automatically{incident.ticket_error ? `: ${incident.ticket_error}` : '.'} Open it by hand.
        </div>
      )}

      <div className="xdr-grid">
        <section className="xdr-card incident-detections-card">
          <header className="xdr-card-head">
            <span className="xdr-card-title">Detections</span>
            <span className="xdr-chip">{detections.length}</span>
          </header>
          <div className="table-responsive">
            <table className="table table-condensed xdr-table">
              <thead>
                <tr>
                  <th>Date (UTC)</th>
                  <th>Severity</th>
                  <th>Detection</th>
                  <th>MITRE ATT&amp;CK</th>
                  <th>Rule</th>
                  <th>
                    <span className="sr-only">Investigate</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {detections.map((d) => (
                  <Fragment key={d.ID}>
                    <tr>
                      <td className="xdr-mono">{d.detected_at}</td>
                      <td dangerouslySetInnerHTML={{ __html: severityHtml(d.severity) }} />
                      <td>{d.title}</td>
                      <td>{d.mitre}</td>
                      <td className="xdr-mono">{d.rule_id}</td>
                      <td className="incident-chain-cell">
                        <button
                          type="button"
                          className="btn btn-link btn-xs incident-chain-toggle"
                          aria-expanded={chainOf === d.ID}
                          aria-controls={`incident-${id}-chain-${d.ID}`}
                          onClick={() => setChainOf(chainOf === d.ID ? null : d.ID)}
                        >
                          <i className="fa fa-sitemap" aria-hidden="true"></i> Process chain
                        </button>
                      </td>
                    </tr>
                    {chainOf === d.ID && (
                      <tr className="incident-chain-row" id={`incident-${id}-chain-${d.ID}`}>
                        <td colSpan={6}>
                          <XdrChain from={{ incident: id }} detection={d.ID} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="xdr-card incident-respond-card">
          <header className="xdr-card-head">
            <span className="xdr-card-title">Respond on {device}</span>
          </header>
          <div className="xdr-card-body">
            {query.data.respond_here === false ? (
              <p className="incident-respond-elsewhere">
                <i className="fa fa-info-circle" aria-hidden="true"></i> This incident belongs to {incident.company_name || 'another company'}.{' '}
                {incident.ticket_id ? (
                  <>
                    Respond from its ticket: <a href={`/app/easeedesk/ticket/${incident.ticket_id}`}>Ticket #{incident.ticket_id}</a>.
                  </>
                ) : (
                  'Open its ticket to respond, or select that company in the header.'
                )}
              </p>
            ) : (
              <RansomwareResponse
                id={incident.device_id}
                title=""
                suspects={false}
                levels
                layout="groups"
                recommended={recommended?.auto_block ? null : (recommended?.action ?? null)}
                onAction={refreshActions}
                between={<RespondSessions id={incident.device_id} onAction={refreshActions} recommended={recommended?.auto_block ? null : (recommended?.action ?? null)} />}
              />
            )}
          </div>
        </section>
      </div>

      <section className="xdr-card incident-actions-card">
        <div className="xdr-card-body">
          <IncidentActions id={id} />
        </div>
      </section>
    </div>
  )
}

const ACTION_LABELS: Record<string, string> = {
  isolate: 'Isolate (full)',
  isolate_internal: 'Isolate from LAN',
  isolate_external: 'Isolate from internet',
  lock: 'Lock screen',
  logoff: 'Log off',
  shutdown: 'Shut down',
  block_ip: 'Block the source IP (from the ticket)',
  disable_account: 'Disable the account (from the ticket)',
}

/** Ce e de facut, dupa cea mai grava detectie; butonul ei e marcat in cardul Respond. */
function Recommended({ r }: { r: NonNullable<SecuriteeIncidentDetail['recommended']> }) {
  if (r.auto_block && r.target) {
    return (
      <div className="xdr-recommend incident-recommended xdr-recommend-none">
        <i className="fas fa-robot" aria-hidden="true"></i>
        <span>{autoBlockText(r.target, r.auto_block)}</span>
      </div>
    )
  }

  return (
    <div className={'xdr-recommend incident-recommended' + (r.action ? '' : ' xdr-recommend-none')}>
      <i className={r.action ? 'fa fa-exclamation-triangle' : 'fa fa-check-circle'} aria-hidden="true"></i>
      <strong>Recommended: {r.action ? (ACTION_LABELS[r.action] ?? r.action) : 'no action on the device'}</strong>
      <span className="xdr-recommend-why">{r.why}</span>
    </div>
  )
}

/** "Response actions" pe aparatul incidentului - aceeasi banda ca pe fila XDR a tichetului. */
function IncidentActions({ id }: { id: number }) {
  const client = useQueryClient()
  const unblock = async (a: IncidentAction) => {
    try {
      client.setQueryData(incidentActionsQuery(id).queryKey, await unblockIncidentAutoBlock(id, a.ID))
      toast('The unblock was sent to the device.', 'Unblock IP', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'The unblock could not be sent.', 'Unblock IP', 'error')
    }
  }

  return <ResponseActionsList query={incidentActionsQuery(id)} onUnblock={unblock} />
}
